import {
  QueryFactory,
  InputTemplateType,
  InputTemplateDto,
  CreateInputTemplateDto,
  PatchInputTemplateDto,
} from '@lmt/shared-types';
import {
  DataGrid,
  DXG,
  RowInsertingEvent,
  RowRemovingEvent,
  RowUpdatingEvent,
  useDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { DxgLoadPanel } from '@lmt/unicorn-shared/app/components/common/dataGrid/DataGrid';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import React, { FC, useRef, useState } from 'react';

import { Loading } from '../../../common/Loading';
import { useEventCallback } from '@mui/material';
import {
  useCachedDataSourceWhenEditing,
  useParallelSaving,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/grid-helper';
import { readFileAsExcel } from '@lmt/unicorn-shared/app/helpers/file-helper';
import { mapExcelRowToDataGrid } from '@lmt/unicorn-shared/app/helpers/excel-to-grid-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { DataGridToolbar } from '../../../common/dataGrid/DataGridToolbar';
import { ColumnButtonClickEvent } from 'devextreme/ui/data_grid';
import styles from './../InputTemplateForm.module.scss';

type Props = {
  subtenantId?: number;
  templateType: InputTemplateType;
  isNumber: boolean;
  fieldId: number | null;
  isMultiline: boolean;
};

const nameof = nameofFactory<InputTemplateDto>();

export const InputTemplatesTable: FC<Props> = (props) => {
  if (!props.subtenantId) {
    return null;
  }
  const modals = useModal();
  const i18n = useScopedTranslation('input_templates.form');
  useDataGrid();

  const {
    data: inputTemplates,
    refetch,
    isLoading,
  } = QueryFactory.InputTemplateQuery.useGetQuery(
    {
      subtenantId: props.subtenantId,
      type: props.templateType,
      dataBlockFieldId: props.fieldId,
    },
    {
      enabled: !!props.subtenantId,
    },
  );
  const dataGridRef = useRef<DataGrid<InputTemplateDto, string>>(null);
  const parallelSaving = useParallelSaving(dataGridRef);
  const dataSource = useCachedDataSourceWhenEditing(
    dataGridRef,
    inputTemplates,
  );

  const onRowCreate = useEventCallback(
    (row: RowInsertingEvent<InputTemplateDto>) => {
      async function onRowCreate(data: InputTemplateDto) {
        const dto: CreateInputTemplateDto = {
          ...data,
          name: data.name.trim(),
          value: data.value.toString(),
          subtenantId: props.subtenantId!,
          dataBlockFieldId: props.fieldId,
          type: props.templateType,
        };
        const finishLoading = parallelSaving.startLoading();
        try {
          await QueryFactory.InputTemplateClient.create(dto);
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          finishLoading();
        }
        await refetch();
        return false;
      }

      row.cancel = onRowCreate(row.data);
    },
  );

  const onRowEdit = useEventCallback(
    (row: RowUpdatingEvent<InputTemplateDto>) => {
      async function onRowEdit(
        oldData: InputTemplateDto,
        data: Partial<InputTemplateDto>,
      ) {
        const inputTemplate = {
          ...oldData,
          ...data,
        };
        const dto: PatchInputTemplateDto = {
          ...inputTemplate,
          name: inputTemplate.name.trim(),
          value: inputTemplate.value.toString(),
        };
        const finishLoading = parallelSaving.startLoading();
        try {
          await QueryFactory.InputTemplateClient.patch(inputTemplate.id, dto);
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          finishLoading();
        }
        await refetch();
        return false;
      }

      row.cancel = onRowEdit(row.oldData, row.newData);
    },
  );

  const onRowDelete = useEventCallback(
    (row: RowRemovingEvent<InputTemplateDto>) => {
      async function onRowDelete(row: InputTemplateDto) {
        const id = row.id;
        const finishLoading = parallelSaving.startLoading();
        try {
          await QueryFactory.InputTemplateClient.remove(id);
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          finishLoading();
        }
        await refetch();
        return false;
      }
      row.cancel = onRowDelete(row.data);
    },
  );

  //#region import
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const inputFileRef = useRef<HTMLInputElement>(null);

  async function onImportExcelFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target!.files![0];
    if (!file) {
      return;
    }
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'xlsx') {
      await modals.showError({
        text: i18n.t('confirmations.invalid_file_error'),
      });
    }
    setIsExcelLoading(true);
    try {
      const values = await readFileAsExcel(file);
      const dataGrid = dataGridRef.current!.instance();
      const rows: InputTemplateDto[] = [];
      const errors: string[] = [];
      let hasDuplicates: boolean = false;
      for (let i = 2; i < values.length; i++) {
        const rowValue = values[i];
        try {
          const row = await mapExcelRowToDataGrid<InputTemplateDto>(
            dataGrid,
            rowValue,
          );
          if (rows.find((x) => x.name === row.name)) {
            hasDuplicates = true;
            errors.push(
              i18n.t('confirmations.row_contains_duplicate_error', {
                index: i,
                name: row.name,
              }),
            );
            continue;
          }

          rows.push(row);
        } catch (e) {
          errors.push(
            `${i18n.t('confirmations.row_parsing_error', { index: i })}: ${convertToErrorString(e)}`,
          );
        }
      }

      if (hasDuplicates) {
        errors.unshift(i18n.t('confirmations.file_contains_duplicates'));
        await modals.showError({
          text: `${errors.join('.<br/>')}.`,
          allowStyleTagsInText: true,
        });
        return;
      }

      if (rows.length === 0 && errors.length === 0) {
        await modals.showAlert({
          text: i18n.t('confirmations.no_data'),
        });
        return;
      }

      const newRows = rows.filter(
        (row) =>
          !inputTemplates?.find(
            (x) =>
              // Bug 74866: explicitly cast to string because row values may have different types,
              // while template values are always strings
              x.name === row.name.toString() &&
              x.value === row.value.toString(),
          ),
      );

      if (newRows.length === 0 && errors.length === 0) {
        await modals.showAlert({
          text: i18n.t('confirmations.all_records_exist'),
        });
        return;
      }

      for (const row of newRows) {
        const currentVisibleRows = dataGrid.getVisibleRows();
        const existingRow = currentVisibleRows.find(
          (item) => item.rowType === 'data' && item.data.name === row.name,
        );
        if (existingRow) {
          // edit row
          const rowIndex = existingRow.rowIndex;
          // In batch mode, we need to use cellValue without editRow to accumulate changes
          // editRow() cancels previous edit when called for another row
          dataGrid.cellValue(rowIndex, 'value', row.value);
        } else {
          // add new row
          const initNewRow = (e: any) => {
            Object.assign(e.data, row);
          };
          dataGrid.on('initNewRow', initNewRow);
          await dataGrid.addRow();
          dataGrid.off('initNewRow', initNewRow);
        }
      }

      if (errors.length > 0) {
        await modals.showError({
          text: `${errors.join('.<br/>')}.`,
          allowStyleTagsInText: true,
        });
      }
      if (newRows.length === 0) {
        await modals.showAlert({
          text: i18n.t('confirmations.all_records_exist'),
        });
      }
    } finally {
      setIsExcelLoading(false);
      // to allow choosing the same file again
      inputFileRef.current!.value = '';
    }
  }
  //#endregion

  // for toolbar
  const [isDirty, setIsDirty] = useState(false);
  const handleContentReady = useEventCallback(() => {
    const grid = dataGridRef.current?.instance();
    if (grid) {
      const hasChanges = grid.hasEditData();
      if (isDirty !== hasChanges) {
        setIsDirty(hasChanges);
      }
    }
  });

  const validateNameUniqueness = useEventCallback((options: any) => {
    const currentName = options.value?.trim();
    if (!currentName) return true;

    const gridInstance = dataGridRef.current?.instance();
    if (!gridInstance) return true;

    const changes = (gridInstance.option('editing.changes') as any[]) || [];

    const currentData = options.data;
    if (!currentData) return true;

    const currentChange = changes.find(
      (ch: any) => ch.key === currentData.id || ch.data === currentData,
    );

    const isNewRow = currentChange?.type === 'insert';
    const isNameTouched = currentChange?.data && 'name' in currentChange.data;

    if (!isNewRow && !isNameTouched) {
      return true;
    }

    const hasDuplicate = gridInstance.getVisibleRows().some((row: any) => {
      if (row.rowType !== 'data') return false;

      const rowChange = changes.find((ch: any) => ch.key === row.key);
      const rowData = rowChange ? { ...row.data, ...rowChange.data } : row.data;

      if (row.key === currentData.id || row.data === currentData) return false;

      const rowName = rowData?.name?.trim();
      return rowName === currentName;
    });

    return !hasDuplicate;
  });

  const onSortingPriorityChanged = useEventCallback(
    async (
      row: ColumnButtonClickEvent<InputTemplateDto>,
      isPinned: boolean,
    ) => {
      const finishLoading = parallelSaving.startLoading();
      try {
        const id = row.row?.data.id;
        if (!id) {
          return;
        }
        await QueryFactory.InputTemplateClient.patch(id, {
          isPinned: isPinned,
        });
        await refetch();
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      } finally {
        finishLoading();
      }
    },
  );

  const onRowPin = useEventCallback(
    (row: ColumnButtonClickEvent<InputTemplateDto>) => {
      onSortingPriorityChanged(row, true).catch((e) =>
        console.error('onRowPin', e),
      );
    },
  );
  const onRowUnpin = useEventCallback(
    (row: ColumnButtonClickEvent<InputTemplateDto>) => {
      onSortingPriorityChanged(row, false).catch((e) =>
        console.error('onRowUnpin', e),
      );
    },
  );

  return (
    <>
      <input
        type="file"
        id="importExcelFile"
        ref={inputFileRef}
        className={'hidden'}
        onChange={onImportExcelFile}
        accept=".xlsx"
      />
      <Loading loading={isExcelLoading || isLoading} title={i18n.t('loading')}>
        <DXG.DataGrid
          ref={dataGridRef}
          dataSource={dataSource}
          keyExpr={'id'}
          showBorders={true}
          style={{ width: '100%', maxHeight: '500px' }}
          onInitNewRow={(e) => {
            const data = e.data;
            data.id = 10000000 + Math.round(Math.random() * 10000);
            data.isPinned = false;
          }}
          onRowInserting={onRowCreate}
          onRowUpdating={onRowEdit}
          onRowRemoving={onRowDelete}
          onCellPrepared={(e) => {
            e.cellElement.style.textAlign = 'left';
          }}
          onContentReady={handleContentReady}
          loadPanel={DxgLoadPanel}
          sorting={{ showSortIndexes: false }}
          wordWrapEnabled={true}
          columnAutoWidth={true}
        >
          <DataGridToolbar
            dataGridRef={dataGridRef}
            inputFileRef={inputFileRef}
            isDirty={isDirty}
            isSingleNewRow={false}
            isExport={true}
            isImport={true}
            exportFileName="TextTemplates"
          />
          <DXG.Paging enabled={false} />
          <DXG.Editing
            mode="batch"
            allowUpdating={true}
            allowAdding={true}
            allowDeleting={true}
            selectTextOnEditStart={true}
            startEditAction="click"
            newRowPosition="pageTop"
          />
          <DXG.Column dataField="id" visible={false} />
          <DXG.Column
            sortOrder="asc"
            width="30%"
            alignment="left"
            dataField={nameof('name')}
            caption={i18n.t('columns.name')}
            dataType="string"
            sortIndex={1}
            allowSorting={false}
            cssClass={styles['top-aligned-cell']}
          >
            <DXG.RequiredRule />
            <DXG.CustomRule
              validationCallback={validateNameUniqueness}
              message={i18n.t('confirmations.duplicate_name')}
            />
          </DXG.Column>
          <DXG.Column
            dataField={nameof('value')}
            caption={i18n.t('columns.value')}
            dataType={props.isNumber ? 'number' : 'string'}
            allowSorting={false}
            cssClass={props.isMultiline ? styles['multiline-cell'] : undefined}
            editCellRender={(cellData: any) => {
              if (props.isNumber) {
                return (
                  <DXG.NumberBox
                    defaultValue={cellData.value}
                    onValueChanged={(e) => cellData.setValue(e.value)}
                  />
                );
              }

              if (!props.isMultiline) {
                return (
                  <DXG.TextBox
                    defaultValue={cellData.value}
                    onValueChanged={(e) => cellData.setValue(e.value)}
                  />
                );
              }

              return (
                <div
                  data-test-id={'multiline-cell'}
                  contentEditable={true}
                  suppressContentEditableWarning={true}
                  className={styles['batch-editable-div']}
                  onBlur={(e) => {
                    const currentText = e.currentTarget.innerText;
                    const originalText = (cellData.value ?? '').toString();
                    if (currentText !== originalText) {
                      cellData.setValue(currentText);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                    }
                  }}
                >
                  {cellData.value}
                </div>
              );
            }}
          >
            <DXG.RequiredRule />
          </DXG.Column>
          <DXG.Column
            dataField="isPinned"
            dataType="boolean"
            visible={false}
            showInColumnChooser={false}
            sortOrder="desc"
            sortIndex={0}
          />
          <DXG.Column type="buttons">
            <DXG.Button name="delete" />
            <DXG.Button
              text={i18n.t('pin_button')}
              visible={isPinVisible as any}
              onClick={onRowPin as any}
              disabled={isDirty}
            />
            <DXG.Button
              text={i18n.t('unpin_button')}
              visible={isUnpinVisible as any}
              onClick={onRowUnpin as any}
              disabled={isDirty}
            />
          </DXG.Column>
        </DXG.DataGrid>
      </Loading>
    </>
  );
};

function isPinVisible(row: any) {
  const data = row.row.data;
  const rowInfo = row.row;

  return !data.isPinned && !rowInfo.isNewRow && !rowInfo.removed;
}

function isUnpinVisible(row: any) {
  const data = row.row.data;
  const rowInfo = row.row;

  return data.isPinned && !rowInfo.isNewRow && !rowInfo.removed;
}
